import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { AsyncSelect, Form, FormField, Input, ModalDialog, Numeric, Select } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { HEAD_TYPES, HeadType, TIP_TYPES, TipType } from "../../../../http/screwConnector/types/common";
import { ScrewFamily } from "../../../../http/screwConnector/types/screwFamilies";
import {
  InsertScrewDiameterBody,
  ResistanceValue,
  ScrewDiameter,
  UpdateScrewDiameterBody,
} from "../../../../http/screwConnector/types/screwDiameters";
import { Washer } from "../../../../http/screwConnector/types/washers";
import useScrewConnectorScrewDiametersApi from "../../../../http/screwConnector/useAdminScrewDiameters";
import useScrewConnectorScrewFamiliesApi from "../../../../http/screwConnector/useAdminScrewFamilies";
import useScrewConnectorWashersApi from "../../../../http/screwConnector/useAdminWashers";
import ResistanceValuesEditor from "./ResistanceValuesEditor";

const MIN_MEASUREMENT_MM = 0.001;

interface ScrewDiameterFormBody {
  screwFamily: ScrewFamily | null;
  diameter: number | null;
  coreDiameter: number | null;
  threadOuterDiameter: number | null;
  headDiameter: number | null;
  headType: HeadType | null;
  tipType: TipType | null;
  bit: string | null;
  yieldMoment: number | null;
  tensileCapacity: number | null;
  washers: Washer[];
  resistanceValues: ResistanceValue[];
}

interface ScrewDiameterFormProps {
  screwDiameter?: ScrewDiameter;
  defaultScrewFamily?: ScrewFamily;
  onClose: () => void;
}

const ScrewDiameterForm: FC<ScrewDiameterFormProps> = ({ screwDiameter, defaultScrewFamily, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;
  const dispatch = useContext(NotificationDispatch);
  const screwDiametersApi = useScrewConnectorScrewDiametersApi();
  const screwFamiliesApi = useScrewConnectorScrewFamiliesApi();
  const washersApi = useScrewConnectorWashersApi();
  const screwFamilyOptions = useAsyncOptions(screwFamiliesApi.getScrewFamilies);
  const washerOptions = useAsyncOptions(washersApi.getWashers);

  const [resolvedWashers, setResolvedWashers] = useState<Washer[]>([]);
  const [resolvedScrewFamily, setResolvedScrewFamily] = useState<ScrewFamily>();

  // washerCodes on the entity are plain strings — resolve them to Washer objects for the select.
  useEffect(() => {
    if (!screwDiameter?.washerCodes.length) {
      setResolvedWashers([]);
      return;
    }
    washersApi
      .getWashers({ limit: 400 }, new AbortController().signal)
      .then((all) => setResolvedWashers(all.filter((w) => screwDiameter.washerCodes.includes(w.code))))
      .catch(() => setResolvedWashers([]));
  }, [screwDiameter?.id]);

  // When editing without a pre-selected family (opened directly on a diameter row), resolve it.
  useEffect(() => {
    if (defaultScrewFamily || !screwDiameter) {
      setResolvedScrewFamily(defaultScrewFamily);
      return;
    }
    const controller = new AbortController();
    screwFamiliesApi
      .getScrewFamilyById(screwDiameter.screwFamilyId, controller.signal)
      .then(setResolvedScrewFamily)
      .catch(() => setResolvedScrewFamily(undefined));
    return () => controller.abort();
  }, [screwDiameter?.id, defaultScrewFamily]);

  const defaultValues: ScrewDiameterFormBody = {
    screwFamily: resolvedScrewFamily ?? null,
    diameter: screwDiameter?.diameter ?? null,
    coreDiameter: screwDiameter?.coreDiameter ?? null,
    threadOuterDiameter: screwDiameter?.threadOuterDiameter ?? null,
    headDiameter: screwDiameter?.headDiameter ?? null,
    headType: screwDiameter?.headType ?? null,
    tipType: screwDiameter?.tipType ?? null,
    bit: screwDiameter?.bit ?? null,
    yieldMoment: screwDiameter?.yieldMoment ?? null,
    tensileCapacity: screwDiameter?.tensileCapacity ?? null,
    washers: resolvedWashers,
    resistanceValues: screwDiameter?.resistanceValues ?? [],
  };

  const onSubmit = () => async (formData: ScrewDiameterFormBody) => {
    if (
      !formData.screwFamily ||
      formData.diameter == null ||
      formData.coreDiameter == null ||
      formData.headDiameter == null ||
      !formData.headType ||
      !formData.bit ||
      formData.yieldMoment == null ||
      formData.tensileCapacity == null
    ) {
      return;
    }

    const body: InsertScrewDiameterBody | UpdateScrewDiameterBody = {
      screwFamilyId: formData.screwFamily.id,
      diameter: formData.diameter,
      coreDiameter: formData.coreDiameter,
      threadOuterDiameter: formData.threadOuterDiameter,
      headDiameter: formData.headDiameter,
      headType: formData.headType,
      tipType: formData.tipType,
      bit: formData.bit,
      yieldMoment: formData.yieldMoment,
      tensileCapacity: formData.tensileCapacity,
      washerCodes: formData.washers.map((w) => w.code),
      resistanceValues: formData.resistanceValues,
    };

    try {
      if (screwDiameter) {
        await screwDiametersApi.updateScrewDiameter(screwDiameter.id, body);
      } else {
        await screwDiametersApi.insertScrewDiameter(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        screwDiameter
          ? `${i18n.translation.common.edit} ${t.screwDiameters.singular}`
          : `${i18n.translation.common.add} ${t.screwDiameters.singular}`
      }
    >
      <Form<ScrewDiameterFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control, register, formState: { errors }, reset, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [screwDiameter]);

          useEffect(() => {
            if (resolvedWashers.length) {
              setValue("washers", resolvedWashers);
            }
          }, [resolvedWashers]);

          useEffect(() => {
            if (resolvedScrewFamily) {
              setValue("screwFamily", resolvedScrewFamily);
            }
          }, [resolvedScrewFamily]);

          return (
            <FormFieldsContainer fullWidth>
              <Controller
                name="screwFamily"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.screwFamily} error={fieldState.error}>
                    {({ labelId }) => (
                      <AsyncSelect<ScrewFamily, false>
                        {...field}
                        {...screwFamilyOptions}
                        inputId={labelId}
                        isMulti={false}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        isInvalid={fieldState.invalid}
                        onChange={(option) => field.onChange(option)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="diameter"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.diameter} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="coreDiameter"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.coreDiameter} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="threadOuterDiameter"
                control={control}
                rules={{ min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.threadOuterDiameter} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="headDiameter"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.headDiameter} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="headType"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.headType} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<{ value: HeadType; label: string }, false>
                        inputId={labelId}
                        options={HEAD_TYPES.map((value) => ({ value, label: t.headTypes[value] }))}
                        value={field.value ? { value: field.value, label: t.headTypes[field.value] } : null}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        isInvalid={isInvalid}
                        onChange={(option) => field.onChange(option?.value ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="tipType"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.tipType} error={fieldState.error} isOptional>
                    {({ labelId, isInvalid }) => (
                      <Select<{ value: TipType; label: string }, false>
                        inputId={labelId}
                        options={TIP_TYPES.map((value) => ({ value, label: t.tipTypes[value] }))}
                        value={field.value ? { value: field.value, label: t.tipTypes[field.value] } : null}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        isClearable
                        isInvalid={isInvalid}
                        onChange={(option) => field.onChange(option?.value ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
              <FormField label={t.screwDiameters.fields.bit} error={errors.bit} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input id={labelId} isInvalid={isInvalid} {...register("bit", { required: !isOptional, minLength: 1 })} />
                )}
              </FormField>
              <Controller
                name="yieldMoment"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.yieldMoment} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitNMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="tensileCapacity"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT_MM }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.tensileCapacity} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT_MM}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitN}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="washers"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={t.screwDiameters.fields.washers} error={fieldState.error} isOptional>
                    {({ labelId }) => (
                      <AsyncSelect<Washer, true>
                        {...washerOptions}
                        inputId={labelId}
                        value={field.value}
                        isMulti={true}
                        placeholder={`${i18n.translation.common.select}...`}
                        getOptionLabel={(option) => option.code}
                        getOptionValue={(option) => option.code}
                        isInvalid={fieldState.invalid}
                        onChange={(options) => field.onChange(Array.from(options))}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="resistanceValues"
                control={control}
                render={({ field }) => (
                  <FormField label={t.screwDiameters.fields.resistanceValues} isOptional>
                    {() => <ResistanceValuesEditor value={field.value} onChange={field.onChange} />}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default ScrewDiameterForm;
